/**
 * useFeatureFlag Hook
 *
 * React hook for checking feature flag status in components.
 * Provides a clean API for feature flag checks without importing config directly.
 *
 * Usage:
 * ```typescript
 * import { useFeatureFlag } from '@/features/integrations/hooks';
 *
 * function MyComponent() {
 *   const isNewSystemEnabled = useFeatureFlag('USE_NEW_INTEGRATION_SYSTEM');
 *   const isJiraEnabled = useFeatureFlag(Integration.jira);
 *
 *   if (!isNewSystemEnabled) {
 *     return <LegacyComponent />;
 *   }
 *
 *   return <NewComponent />;
 * }
 * ```
 */

import {
  isFeatureEnabled,
  isIntegrationEnabled,
} from '@/features/integrations/config/featureFlags.config';
import type { FeatureFlags } from '@/features/integrations/types/configuration.types';
import { Integration } from '@/types';

/**
 * Check if a feature flag is enabled
 *
 * @param flag - Feature flag name or integration ID
 * @returns true if feature is enabled
 *
 * @example
 * // Check system-wide flag
 * const useNewSystem = useFeatureFlag('USE_NEW_INTEGRATION_SYSTEM');
 *
 * // Check integration-specific flag
 * const useNewJira = useFeatureFlag(Integration.jira);
 */
export function useFeatureFlag(
  flag: keyof FeatureFlags | Integration
): boolean {
  // Check if it's a system-wide feature flag
  if (flag === 'USE_NEW_INTEGRATION_SYSTEM') {
    return isFeatureEnabled(flag);
  }

  // Otherwise treat as integration ID
  return isIntegrationEnabled(flag as Integration);
}
